"use client";

import React, { useState, useEffect } from "react";
import { Menu, X, ArrowRight, Sparkles } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";

/**
 * Header.tsx (v3 — FINAL) — wie v2, neuer Pill-Text:
 *  "✨ In unter 1 Stunde live"
 * Tooltip auf Hover:
 *  "Diese Seite war in unter 1 Stunde live — gebaut mit PAGEMAKER24. Klick → selbst starten."
 * Klick → dashboard.pagemaker24.ch/sign-up
 *
 * Drop-in für: src/reusable_sections/Header.tsx
 */

const navItems = [{
  label: "Produkt",
  href: "#technologie"
}, {
  label: "Beispiele",
  href: "#inspirierende-kmu-beispiele"
}, {
  label: "Preise",
  href: "#preise"
}, {
  label: "Für Agenturen",
  href: "#skalieren-integrieren"
}, {
  label: "Funktionen",
  href: "#alles-in-einem-funktionen"
}];
const SIGNUP_URL = "https://dashboard.pagemaker24.ch/sign-up";
const PILL_TOOLTIP = "Diese Seite war in unter 1 Stunde live — gebaut mit PAGEMAKER24. Klick → selbst starten.";
export default function Pagemaker24Navigation() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [showBanner, setShowBanner] = useState(true);
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);
  useEffect(() => {
    const handleAnchorClick = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      const anchor = target.closest("a[href]") as HTMLAnchorElement | null;
      if (anchor) {
        const href = anchor.getAttribute("href");
        if (!href) return;
        if (href === "/#") {
          e.preventDefault();
          window.scrollTo({
            top: 0,
            behavior: "smooth"
          });
          return;
        }
        let hash = "";
        if (href.startsWith("#")) {
          hash = href;
        } else if (href.startsWith("/") && href.includes("#")) {
          const [path, hashPart] = href.split("#");
          if (path === "/" || path === window.location.pathname) {
            hash = "#" + hashPart;
          }
        }
        if (hash && hash !== "#") {
          const element = document.querySelector(hash);
          if (element) {
            e.preventDefault();
            element.scrollIntoView({
              behavior: "smooth",
              block: "start"
            });
            setIsMobileMenuOpen(false);
          }
        }
      }
    };
    document.addEventListener("click", handleAnchorClick);
    return () => document.removeEventListener("click", handleAnchorClick);
  }, []);
  useEffect(() => {
    if (isMobileMenuOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "unset";
    }
    return () => {
      document.body.style.overflow = "unset";
    };
  }, [isMobileMenuOpen]);
  return <section id="pagemaker24-header" className="bg-background text-foreground">
      {/* Info Banner */}
      {showBanner && <div className="relative bg-[hsl(16,88%,54%)] text-white text-center text-sm py-2.5 px-4">
          <div className="container mx-auto flex items-center justify-center gap-2 flex-wrap">
            <span className="font-medium">
              Jetzt 3 Tage kostenlos testen — keine Kreditkarte nötig.
            </span>
            <a href={SIGNUP_URL} className="inline-flex items-center gap-1 underline underline-offset-2 font-semibold hover:opacity-90 transition-opacity">
              Gratis starten
              <ArrowRight className="w-3.5 h-3.5" />
            </a>
          </div>
          <button onClick={() => setShowBanner(false)} className="absolute right-3 top-1/2 -translate-y-1/2 text-white/70 hover:text-white transition-colors" aria-label="Banner schliessen">
            <X className="w-4 h-4" />
          </button>
        </div>}

      {/* Header */}
      <header className={`sticky top-0 z-50 w-full transition-all duration-300 border-b ${isScrolled ? "bg-background/80 backdrop-blur-md border-border/50 shadow-sm" : "bg-background border-transparent"}`}>
        <div className="container mx-auto px-4 h-20 flex items-center justify-between">
          {/* Logo + Built-with-Pill */}
          <div className="flex items-center gap-3 z-50 relative">
            <Link to="/HomePage" className="flex items-center">
              <img src="https://pagemaker.my-pagemaker24.com/wp-content/uploads/2026/04/Pagemaker_Logo_v2.png" alt="PAGEMAKER24" className="h-7 w-auto object-contain" />
            </Link>

            {/* Desktop Pill — neuer Text */}
            <a href={SIGNUP_URL} title={PILL_TOOLTIP} aria-label={PILL_TOOLTIP} className="hidden sm:inline-flex items-center gap-1.5 bg-[hsl(16,88%,54%)]/10 hover:bg-[hsl(16,88%,54%)]/15 border border-[hsl(16,88%,54%)]/25 text-[hsl(16,88%,54%)] rounded-full px-2.5 py-1 transition-all hover:scale-[1.03]">
              <Sparkles className="w-3 h-3" strokeWidth={2.5} />
              <span className="text-[10px] font-semibold tracking-wide whitespace-nowrap">
                In unter 1 Stunde live
              </span>
            </a>

            {/* Mobile-only Sparkle-Dot mit Tooltip via title */}
            <a href={SIGNUP_URL} title={PILL_TOOLTIP} aria-label={PILL_TOOLTIP} className="sm:hidden inline-flex items-center justify-center w-6 h-6 rounded-full bg-[hsl(16,88%,54%)]/10 border border-[hsl(16,88%,54%)]/25 text-[hsl(16,88%,54%)]">
              <Sparkles className="w-3 h-3" strokeWidth={2.5} />
            </a>
          </div>

          {/* Desktop Nav */}
          <nav className="hidden md:flex items-center gap-6">
            {navItems.map(item => <a key={item.label} href={item.href} className="relative font-sans font-medium text-foreground px-0 py-2 after:absolute after:bottom-0 after:left-0 after:h-[2px] after:w-0 after:bg-primary after:transition-all after:duration-300 hover:after:w-full hover:text-primary transition-colors text-sm">
                {item.label}
              </a>)}
          </nav>

          {/* CTA + Mobile Toggle */}
          <div className="flex items-center gap-4 z-50 relative">
            <a href={SIGNUP_URL} className="hidden md:inline-flex items-center justify-center gap-2 whitespace-nowrap text-sm font-medium rounded-md px-4 py-2 shadow-lg shadow-primary/10 transition-all duration-300 text-white hover:opacity-90 hover:scale-105" style={{
            backgroundColor: "hsl(16,88%,54%)"
          }}>
              Kostenlos starten
              <ArrowRight className="w-4 h-4" />
            </a>

            <Button variant="ghost" size="icon" className="md:hidden text-foreground hover:text-primary transition-colors" onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)} aria-label="Menü umschalten">
              {isMobileMenuOpen ? <X className="size-6" /> : <Menu className="size-6" />}
            </Button>
          </div>
        </div>
      </header>

      {/* Mobile Menu */}
      {isMobileMenuOpen && <div className="fixed inset-0 z-40 bg-background/98 backdrop-blur-md pt-24 pb-6 overflow-y-auto md:hidden animate-in fade-in slide-in-from-top-4 duration-300">
          <div className="container mx-auto">
            <nav className="flex flex-col gap-2 p-4">
              {navItems.map(item => <a key={item.label} href={item.href} onClick={() => setIsMobileMenuOpen(false)} className="block px-4 py-3 text-lg font-sans font-medium text-foreground hover:bg-accent hover:text-primary rounded-md transition-colors">
                  {item.label}
                </a>)}
              <div className="mt-4 px-4">
                <a href={SIGNUP_URL} onClick={() => setIsMobileMenuOpen(false)} className="flex items-center justify-center gap-2 w-full rounded-md text-white font-medium py-3 text-base transition-all hover:opacity-90" style={{
              backgroundColor: "hsl(16,88%,54%)"
            }}>
                  Kostenlos starten
                  <ArrowRight className="w-4 h-4" />
                </a>
              </div>

              {/* Mobile-Built-with-Hinweis */}
              <a href={SIGNUP_URL} onClick={() => setIsMobileMenuOpen(false)} className="mt-6 mx-4 inline-flex items-center justify-center gap-2 bg-[hsl(16,88%,54%)]/10 border border-[hsl(16,88%,54%)]/25 text-[hsl(16,88%,54%)] rounded-full px-3 py-2 text-xs font-medium">
                <Sparkles className="w-3 h-3" strokeWidth={2.5} />
                Diese Seite war in unter 1 Stunde live — selbst starten
              </a>
            </nav>
          </div>
        </div>}
    </section>;
}